import { useEffect, useState } from "react"
import { useUser } from "@/apis/bff"
import { Banner } from "@/components/ui/banner"
import { SofiaEnvironment } from "@/sofia-environment"
import { useBannerStore } from "@/stores/useBannerStore"

type HealthStatus = "Healthy" | "Unhealthy" | "Degraded"

type HealthCheckEntry = {
  id: number
  name: string
  status: HealthStatus
  description: string
}

type HealthCheckReport = {
  id: number
  status: HealthStatus
  name: string
  entries: HealthCheckEntry[]
}

const FEATURE_NAME = "HealthCheckWarningBanner"

const {
  Bff: { Url: bffHost, Credentials: credentials },
  DisabledFeatures: disabledFeatures,
} = SofiaEnvironment

const ignoredUnhealthyServiceKeys = [
  "web-bff > Database: session-db",
  "web-bff > Downstream: ch-api",
  "web-bff > Downstream: common-api",
  "web-bff > Downstream: de-api",
  "patient-portal-bff > Endpoint",
  "mobile-bff > Endpoint",
  "common-api",
  "ch-api",
  "de-api",
]

const unhealthyServiceMessages: Readonly<Record<string, string | null>> = {
  // Unhealthy messages for web-bff
  "web-bff": "There might be internal errors between parts of Sofia.",
  "web-bff > Database: session-db": null,
  "web-bff > Downstream: ch-api": null,
  "web-bff > Downstream: common-api": null,
  "web-bff > Downstream: de-api": null,

  // Unhealthy messages for patient-portal-bff
  "patient-portal-bff": "Patients might be unable to access their portal.",
  "patient-portal-bff > Endpoint": null,

  // Unhealthy messages for mobile-bff
  "mobile-bff": "Patients might face issues on the mobile app.",
  "mobile-bff > Endpoint": null,

  // Unhealthy messages for common-api
  "common-api": null,
  "common-api > Atoss Synchronization": "ATOSS shifts/skills data might be outdated (no recent import).",
  "common-api > AzureServiceBus": "There might be internal errors in common part of Sofia.",
  "common-api > Blob storage: AtossImport": "New ATOSS imports might not be working.",
  "common-api > Blob storage: ChatTranscriptCh":
    "There might be issues to save/retrieve chat transcripts in Switzerland.",
  "common-api > Blob storage: ChatTranscriptDe": "There might be issues to save/retrieve chat transcripts in Germany.",
  "common-api > Blob storage: PatientFileServiceCh":
    "There might be issues to view patient files, or to produce documents for patients in Switzerland.",
  "common-api > Blob storage: PatientFileServiceDe":
    "There might be issues to view patient files, or to produce documents for patients in Germany.",
  "common-api > Blob storage: UploadFileService": "Patients might be unable to upload appointment's attachments.",
  "common-api > Database: common-db": "There might be internal errors in common part of Sofia.",
  "common-api > Redis": "There might be internal errors in common part of Sofia.",
  "common-api > Smtp": "Emails might not be delivered to patients.",
  "common-api > SurveyLab": "Questionnaires might not be available for patients.",
  "common-api > User Synchronization": null,
  "common-api > OutboxProcessing":
    "Background processing is failing. Notifications and async operations might not be delivered.",

  // Unhealthy messages for ch-api
  "ch-api": null,
  "ch-api > AzureServiceBus": "There might be internal errors in common part of Sofia.",
  "ch-api > BetterDoc": "BetterDoc integration might not work.",
  "ch-api > Blob storage: InsuranceImport": "Processing partner insurance data might fail.",
  "ch-api > Blob storage: PatientFileService":
    "There might be issues to view patient files, or to produce documents for patients in Switzerland.",
  "ch-api > Blob storage: StaticFileService": "There might be issues to produce documents in Switzerland.",
  "ch-api > Blob storage: UploadFileService": "Medical reports might not be able to be attached in Swizerland.",
  "ch-api > Database: care-provider-db": "There might be internal errors in common part of Sofia.",
  "ch-api > Database: ch-db": "There might be internal errors in common part of Sofia.",
  "ch-api > DataGuard": "Accessing patient consents might not work.",
  "ch-api > Documedis Synchronization":
    "Searching for medications in Switzerland might offer outdated results (no recent Documedis import).",
  "ch-api > ElectronicPrescriptionCertifaction":
    "Producing ePrescriptions might not work. Doctors can revert to normal prescriptions in the meantime.",
  "ch-api > EPost": "Welcome letters might not be sent to insured persons.",
  "ch-api > HinSigningCertifaction":
    "HIN Signing might not work. Doctors can revert to ePrescriptions in the meantime.",
  "ch-api > Redis": "There might be internal errors in common part of Sofia.",
  "ch-api > Sms via eCall": "SMS might not be delivered to patients.",
  "ch-api > OutboxProcessing":
    "Background processing is failing. Notifications and async operations might not be delivered.",

  // Unhealthy messages for de-api
  "de-api": null,
  "de-api > AzureServiceBus": "There might be internal errors in common part of Sofia.",
  "de-api > Blob storage: PatientFileService":
    "There might be issues to view patient files, or to produce documents for patients in Germany.",
  "de-api > Blob storage: StaticFileService": "There might be issues to produce documents in Germany.",
  "de-api > Blob storage: UploadFileService": "Medical reports might not be able to be attached in Germany.",
  "de-api > Database: de-db": "There might be internal errors in common part of Sofia.",
  "de-api > DataGuard": "Accessing patient consents might not work.",
  "de-api > Mia": "Searching for medicaments in Germany might not work.",
  "de-api > Redis": "There might be internal errors in common part of Sofia.",
  "de-api > Sms via eCall": "SMS might not be delivered to patients.",
  "de-api > OutboxProcessing":
    "Background processing is failing. Notifications and async operations might not be delivered.",
}

export const HealthCheckWarningBanner = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [unhealthyServiceKeys, setUnhealthyServiceKeys] = useState<string[]>([])
  const [healthCheckDescriptions, setHealthCheckDescriptions] = useState<Map<string, string>>(new Map())
  const user = useUser()

  const isMedicalSupervisor = user.isOneOf(["MedicalSupervisor"])
  const isFeatureFlagDisabled = disabledFeatures.includes(FEATURE_NAME)

  const bannerId = "health-check-warning"
  const isDismissed = useBannerStore((state) => state.banners[bannerId])
  const dismissBanner = useBannerStore((state) => state.dismissBanner)

  async function fetchHealthCheckReport() {
    try {
      const healthCheckReport = await fetch(`${bffHost}/status/api`, {
        headers: { "X-CSRF": "1" },
        credentials: credentials,
      })

      const healCheckData: HealthCheckReport[] = await healthCheckReport.json()
      return healCheckData
    } catch {
      throw new Error("Error fetching health check report")
    }
  }

  useEffect(() => {
    if (isFeatureFlagDisabled || !isMedicalSupervisor) {
      return
    }

    const fetchData = async () => {
      try {
        const healthCheckReport = await fetchHealthCheckReport()
        const unhealthyServices: string[] = []
        const descriptions = new Map<string, string>()

        for (const item of healthCheckReport) {
          // A server is healthy if all its services are healthy
          if (item.status === "Healthy") continue

          if (!ignoredUnhealthyServiceKeys.includes(item.name)) {
            unhealthyServices.push(item.name)
          }

          for (const entry of item.entries) {
            if (entry.status === "Healthy") continue

            const key = `${item.name} > ${entry.name}`
            if (!ignoredUnhealthyServiceKeys.includes(key)) {
              unhealthyServices.push(key)
              if (entry.description) {
                descriptions.set(key, entry.description)
              }
            }
          }
        }

        if (unhealthyServices.length) {
          setShowBanner(true)
          setUnhealthyServiceKeys(unhealthyServices)
          setHealthCheckDescriptions(descriptions)
        } else {
          setShowBanner(false)
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()

    const interval = setInterval(fetchData, 600000)

    return () => clearInterval(interval)
  }, [isFeatureFlagDisabled, isMedicalSupervisor])

  if (isDismissed) return null

  return (
    showBanner && (
      <Banner
        type="full-width"
        variant="warning"
        dismissOptions={{
          onDismiss: () => dismissBanner(bannerId),
        }}>
        <div className="flex flex-col">
          <div>
            <a href={`${bffHost}/status#/healthchecks`} target="_blank" className="underline" rel="noreferrer">
              Some automated checks failed
            </a>
            ,&nbsp;contact IT-Helpdesk if the problem persists.
          </div>
          <div>
            {unhealthyServiceKeys.map((key) => {
              const backendDescription = healthCheckDescriptions.get(key)
              const predefinedMessage = unhealthyServiceMessages[key]
              const message = backendDescription || predefinedMessage

              return message ? (
                <div key={key}>
                  <b>{key}:</b> {message}
                </div>
              ) : null
            })}
          </div>
        </div>
      </Banner>
    )
  )
}
